"use client";

// Vendored from AI SDK Elements (https://elements.ai-sdk.dev/components/context).
// Adapted to use our Button/HoverCard primitives, a plain-div progress bar in
// place of Radix Progress, and `calculateTokenCostUsd` from `@quovy/usage`
// instead of `tokenlens` for cost math (so costs stay consistent with the rest
// of the app's pricing rollups). The `usage` prop is a narrow structural type
// covering only the fields the component actually reads — that decouples us
// from AI SDK minor version bumps like V2→V3's `inputTokenDetails` refactor.

import type { ComponentProps, ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

import { modelPricing } from "@quovy/types";

import { Button } from "../inputs/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../primitives/hover-card";
import { cn } from "../utils";
import {
  calculateTokenCostUsd,
  formatTokensCompact,
  formatUsd,
  type TokenUsage,
} from "@quovy/usage";

const PERCENT_MAX = 100;
const ICON_RADIUS = 10;
const ICON_VIEWBOX = 24;
const ICON_CENTER = 12;
const ICON_STROKE_WIDTH = 2;

export type ContextUsage = {
  inputTokens?: number | undefined;
  outputTokens?: number | undefined;
  reasoningTokens?: number | undefined;
  cachedInputTokens?: number | undefined;
};

type ContextSchema = {
  usedTokens: number;
  maxTokens: number;
  usage?: ContextUsage | undefined;
  modelId?: string | undefined;
};

const ContextContext = createContext<ContextSchema | null>(null);

const useContextValue = () => {
  const context = useContext(ContextContext);
  if (!context) {
    throw new Error("Context components must be used within Context");
  }
  return context;
};

const formatPercent = (fraction: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 1, style: "percent" }).format(fraction);

// Returns `undefined` when the model is unknown to the registry, so callers
// can distinguish "unpriced model" from "$0 cost" and render accordingly.
// `calculateTokenCostUsd` silently returns "0" for unknown models, which
// would otherwise mislead users by displaying $0.00 for a real cost.
function costFor(modelId: string | undefined, usage: TokenUsage): number | undefined {
  if (!modelId) return undefined;
  const pricing = modelPricing(modelId);
  if (!pricing) return undefined;
  const value = Number.parseFloat(calculateTokenCostUsd(pricing, usage));
  return Number.isFinite(value) ? value : undefined;
}

const UNPRICED_COST_LABEL = "—";

function formatOptionalCost(cost: number | undefined): string {
  return cost === undefined ? UNPRICED_COST_LABEL : formatUsd(cost);
}

/** Row-display split: input minus its cached portion, which gets its own row. */
function uncachedInputTokens(usage: ContextUsage | undefined): number {
  const total = usage?.inputTokens ?? 0;
  const cached = usage?.cachedInputTokens ?? 0;
  return Math.max(0, total - cached);
}

export type ContextProps = ComponentProps<typeof HoverCard> & ContextSchema;

export const Context = ({ usedTokens, maxTokens, usage, modelId, ...props }: ContextProps) => {
  const contextValue = useMemo(
    () => ({ maxTokens, modelId, usage, usedTokens }),
    [maxTokens, modelId, usage, usedTokens],
  );

  return (
    <ContextContext.Provider value={contextValue}>
      <HoverCard closeDelay={0} openDelay={0} {...props} />
    </ContextContext.Provider>
  );
};

const ContextIcon = () => {
  const { usedTokens, maxTokens } = useContextValue();
  const circumference = 2 * Math.PI * ICON_RADIUS;
  const usedPercent = maxTokens > 0 ? usedTokens / maxTokens : 0;
  const dashOffset = circumference * (1 - Math.min(1, Math.max(0, usedPercent)));

  return (
    <svg
      aria-label="Model context usage"
      height="20"
      role="img"
      style={{ color: "currentcolor" }}
      viewBox={`0 0 ${ICON_VIEWBOX} ${ICON_VIEWBOX}`}
      width="20"
    >
      <circle
        cx={ICON_CENTER}
        cy={ICON_CENTER}
        fill="none"
        opacity="0.25"
        r={ICON_RADIUS}
        stroke="currentColor"
        strokeWidth={ICON_STROKE_WIDTH}
      />
      <circle
        cx={ICON_CENTER}
        cy={ICON_CENTER}
        fill="none"
        opacity="0.7"
        r={ICON_RADIUS}
        stroke="currentColor"
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
        strokeWidth={ICON_STROKE_WIDTH}
        style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
      />
    </svg>
  );
};

const DONUT_RADIUS = 9;
const DONUT_STROKE_WIDTH = 2.5;

/** The usage ring with the percentage at its center — for an icon-sized trigger with no room for a separate label. */
export const ContextUsageDonut = ({ className }: { className?: string }) => {
  const { usedTokens, maxTokens } = useContextValue();
  const usedPercent = Math.min(1, Math.max(0, maxTokens > 0 ? usedTokens / maxTokens : 0));
  const circumference = 2 * Math.PI * DONUT_RADIUS;

  return (
    <svg
      viewBox={`0 0 ${ICON_VIEWBOX} ${ICON_VIEWBOX}`}
      className={cn("size-6", className)}
      aria-hidden="true"
    >
      <circle
        cx={ICON_CENTER}
        cy={ICON_CENTER}
        r={DONUT_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={DONUT_STROKE_WIDTH}
        opacity={0.3}
      />
      <circle
        cx={ICON_CENTER}
        cy={ICON_CENTER}
        r={DONUT_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={DONUT_STROKE_WIDTH}
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={circumference * (1 - usedPercent)}
        strokeLinecap="round"
        style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
      />
      <text
        x={ICON_CENTER}
        y={ICON_CENTER}
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        fontSize={7.5}
        fontWeight={600}
      >
        {Math.round(usedPercent * 100)}
      </text>
    </svg>
  );
};

export type ContextTriggerProps = ComponentProps<typeof Button>;

export const ContextTrigger = ({ children, ...props }: ContextTriggerProps) => {
  const { usedTokens, maxTokens } = useContextValue();
  const usedPercent = maxTokens > 0 ? usedTokens / maxTokens : 0;
  const renderedPercent = formatPercent(usedPercent);

  return (
    <HoverCardTrigger asChild>
      {children ?? (
        <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 px-2" {...props}>
          <span className="font-medium text-muted-foreground">{renderedPercent}</span>
          <ContextIcon />
        </Button>
      )}
    </HoverCardTrigger>
  );
};

export type ContextContentProps = ComponentProps<typeof HoverCardContent>;

export const ContextContent = ({ className, ...props }: ContextContentProps) => (
  <HoverCardContent className={cn("min-w-60 divide-y overflow-hidden p-0", className)} {...props} />
);

export type ContextContentHeaderProps = ComponentProps<"div">;

export const ContextContentHeader = ({
  children,
  className,
  ...props
}: ContextContentHeaderProps) => {
  const { usedTokens, maxTokens } = useContextValue();
  const usedPercent = maxTokens > 0 ? usedTokens / maxTokens : 0;
  const displayPct = formatPercent(usedPercent);
  const used = formatTokensCompact(usedTokens);
  const total = formatTokensCompact(maxTokens);
  const barWidth = Math.min(100, Math.max(0, usedPercent * PERCENT_MAX));

  return (
    <div className={cn("w-full space-y-2 p-3", className)} {...props}>
      {children ?? (
        <>
          <div className="flex items-center justify-between gap-3 text-xs">
            <p>{displayPct}</p>
            <p className="font-mono text-muted-foreground">
              {used} / {total}
            </p>
          </div>
          <div aria-hidden className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-[width]"
              style={{ width: `${barWidth}%` }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export type ContextContentBodyProps = ComponentProps<"div">;

export const ContextContentBody = ({ children, className, ...props }: ContextContentBodyProps) => (
  <div className={cn("w-full space-y-1.5 p-3", className)} {...props}>
    {children}
  </div>
);

export type ContextContentFooterProps = ComponentProps<"div">;

export const ContextContentFooter = ({
  children,
  className,
  ...props
}: ContextContentFooterProps) => {
  const { modelId, usage } = useContextValue();
  // `input`/`output` are inclusive totals — calculateTokenCostUsd subtracts
  // cacheRead/reasoning back out itself, so passing the already-uncached
  // amount here would double-subtract cached tokens and under-bill the run.
  const costUsd = costFor(modelId, {
    input: usage?.inputTokens ?? 0,
    output: usage?.outputTokens ?? 0,
    ...(usage?.reasoningTokens ? { reasoning: usage.reasoningTokens } : {}),
    ...(usage?.cachedInputTokens ? { cacheRead: usage.cachedInputTokens } : {}),
  });
  const totalCost = formatOptionalCost(costUsd);
  const totalLabel = costUsd === undefined ? "Cost" : "Total cost";

  return (
    <div
      className={cn(
        "flex w-full items-center justify-between gap-3 bg-secondary p-3 text-xs",
        className,
      )}
      {...props}
    >
      {children ?? (
        <>
          <span className="text-muted-foreground">{totalLabel}</span>
          <span>{totalCost}</span>
        </>
      )}
    </div>
  );
};

const TokensWithCost = ({ tokens, costText }: { tokens?: number; costText?: string }) => (
  <span>
    {tokens === undefined ? "—" : formatTokensCompact(tokens)}
    {costText ? <span className="ml-2 text-muted-foreground">• {costText}</span> : null}
  </span>
);

export type ContextInputUsageProps = ComponentProps<"div">;

export const ContextInputUsage = ({ className, children, ...props }: ContextInputUsageProps) => {
  const { usage, modelId } = useContextValue();
  // Display and bill the uncached portion only; cached tokens get their own
  // row via `ContextCacheUsage`, so the two rows together sum to the total.
  const uncached = uncachedInputTokens(usage);

  if (children !== undefined) return children as ReactNode;
  if (!uncached) return null;

  const cost = costFor(modelId, { input: uncached, output: 0 });

  return (
    <div className={cn("flex items-center justify-between text-xs", className)} {...props}>
      <span className="text-muted-foreground">Input</span>
      <TokensWithCost costText={formatOptionalCost(cost)} tokens={uncached} />
    </div>
  );
};

export type ContextOutputUsageProps = ComponentProps<"div">;

export const ContextOutputUsage = ({ className, children, ...props }: ContextOutputUsageProps) => {
  const { usage, modelId } = useContextValue();
  const outputTokens = usage?.outputTokens ?? 0;

  if (children !== undefined) return children as ReactNode;
  if (!outputTokens) return null;

  const cost = costFor(modelId, { input: 0, output: outputTokens });

  return (
    <div className={cn("flex items-center justify-between text-xs", className)} {...props}>
      <span className="text-muted-foreground">Output</span>
      <TokensWithCost costText={formatOptionalCost(cost)} tokens={outputTokens} />
    </div>
  );
};

export type ContextReasoningUsageProps = ComponentProps<"div">;

export const ContextReasoningUsage = ({
  className,
  children,
  ...props
}: ContextReasoningUsageProps) => {
  const { usage, modelId } = useContextValue();
  const reasoningTokens = usage?.reasoningTokens ?? 0;

  if (children !== undefined) return children as ReactNode;
  if (!reasoningTokens) return null;

  const cost = costFor(modelId, { input: 0, output: 0, reasoning: reasoningTokens });

  return (
    <div className={cn("flex items-center justify-between text-xs", className)} {...props}>
      <span className="text-muted-foreground">Reasoning</span>
      <TokensWithCost costText={formatOptionalCost(cost)} tokens={reasoningTokens} />
    </div>
  );
};

export type ContextCacheUsageProps = ComponentProps<"div">;

export const ContextCacheUsage = ({ className, children, ...props }: ContextCacheUsageProps) => {
  const { usage, modelId } = useContextValue();
  const cacheTokens = usage?.cachedInputTokens ?? 0;

  if (children !== undefined) return children as ReactNode;
  if (!cacheTokens) return null;

  const cost = costFor(modelId, { input: 0, output: 0, cacheRead: cacheTokens });

  return (
    <div className={cn("flex items-center justify-between text-xs", className)} {...props}>
      <span className="text-muted-foreground">Cache</span>
      <TokensWithCost costText={formatOptionalCost(cost)} tokens={cacheTokens} />
    </div>
  );
};

/** Compact `12K / 200K` trigger for table cells; the composer keeps the percent+ring default. */
export const ContextTokenCount = ({ className }: { className?: string }) => {
  const { usedTokens, maxTokens } = useContextValue();
  const used = formatTokensCompact(usedTokens);
  const total = formatTokensCompact(maxTokens);
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1 text-sm tabular-nums text-muted-foreground hover:text-foreground",
        className,
      )}
      aria-label={`${used} of ${total} context tokens`}
    >
      {used}
      <span className="text-muted-foreground/70">/</span>
      {total}
    </button>
  );
};

/** Standard usage popover body — composer footer and the Threads table share this. */
export const ContextUsageDetails = (props: ContextContentProps) => (
  <ContextContent {...props}>
    <ContextContentHeader />
    <ContextContentBody>
      <ContextInputUsage />
      <ContextOutputUsage />
      <ContextReasoningUsage />
      <ContextCacheUsage />
    </ContextContentBody>
    <ContextContentFooter />
  </ContextContent>
);
